/**
 * PromotionSection Component
 *
 * A section that displays promotional banners to encourage users to become mentors
 * or subscribe to premium services.
 * Features a full-width carousel with centered items and smooth scrolling navigation.
 * Updated to use shadcn carousel component for smooth scrolling.
 */
import type { StaticImageData } from 'next/image';
import React, { useEffect, useState } from 'react';

import { cn } from '@ui/lib/utils';

import { Section } from '../../components/common/section/Section';
import { PromotionBanner } from '../../components/features/landing/promotionBanner';
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from '../../components/ui/carousel';

export interface PromotionItem {
  /**
   * Unique identifier for the promotion
   */
  id: string;

  /**
   * Title for the promotion banner
   */
  title?: string;

  /**
   * Description text for the promotion banner
   */
  description?: string;

  /**
   * Button label text
   */
  buttonLabel?: string;

  /**
   * Optional image URL or StaticImageData to override the default banner image
   */
  imageUrl?: string | StaticImageData;
}

export interface PromotionSectionProps {
  /**
   * Array of promotion items to display
   * For backward compatibility, you can also provide individual properties instead of an array
   */
  promotions?: PromotionItem[];

  /**
   * Title for the promotion banner (for backward compatibility)
   */
  title?: string;

  /**
   * Description text for the promotion banner (for backward compatibility)
   */
  description?: string;

  /**
   * Button label text (for backward compatibility)
   */
  buttonLabel?: string;

  /**
   * Handler for when a promotion button is clicked
   * Receives the promotion id as a parameter
   */
  onButtonClick?: (promotionId: string) => void;

  /**
   * Optional image URL or StaticImageData to override the default banner image (for backward compatibility)
   */
  imageUrl?: string | StaticImageData;

  /**
   * Additional CSS class names
   */
  className?: string;
}

/**
 * Default mock promotions to use when none are provided
 * Using three identical cards for demo purposes
 */
const DEFAULT_PROMOTIONS: PromotionItem[] = [
  {
    id: 'promo-1',
    title: 'Unlock Your Exclusive 30-Day Trial!',
    description: 'Subscribe now and explore all features risk-free.',
    buttonLabel: 'Become a Mentor'
  },
  {
    id: 'promo-2',
    title: 'Unlock Your Exclusive 30-Day Trial!',
    description: 'Subscribe now and explore all features risk-free.',
    buttonLabel: 'Become a Mentor'
  },
  {
    id: 'promo-3',
    title: 'Unlock Your Exclusive 30-Day Trial!',
    description: 'Subscribe now and explore all features risk-free.',
    buttonLabel: 'Become a Mentor'
  }
];

/**
 * PromotionSection component for displaying promotional banners with horizontal scrolling
 *
 * @example
 * ```tsx
 * <PromotionSection
 *   promotions={myPromotions}
 *   onButtonClick={(id) => console.log(`Promotion ${id} clicked`)}
 * />
 * ```
 */
export const PromotionSection: React.FC<PromotionSectionProps> = ({
  promotions,
  title,
  description,
  buttonLabel,
  onButtonClick,
  imageUrl,
  className = ''
}) => {
  // Support both new array format and old single-promotion format
  const effectivePromotions =
    promotions ||
    (title || description || buttonLabel || imageUrl
      ? [{ id: 'legacy-promo', title, description, buttonLabel, imageUrl }]
      : DEFAULT_PROMOTIONS);

  // Create a wrapper function for backward compatibility
  const handleButtonClick = (promotionId: string) => {
    if (onButtonClick) {
      // If using the legacy format, don't pass ID
      if (!promotions && promotionId === 'legacy-promo') {
        // @ts-expect-error - for backward compatibility with legacy function signature
        onButtonClick();
      } else {
        onButtonClick(promotionId);
      }
    }
  };

  // Carousel state management
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);

  // Update current index when carousel changes
  useEffect(() => {
    if (!api) {
      return;
    }

    setCurrent(api.selectedScrollSnap());

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap());
    });
  }, [api]);

  // Scroll to a specific slide
  const scrollToSlide = (index: number) => {
    if (!api || index < 0 || index >= effectivePromotions.length) return;
    api.scrollTo(index);
  };

  return (
    <Section disableVerticalPadding className={cn('py-6 md:py-15', className)}>
      {/* Full-width carousel with centering */}
      <div className="relative w-full">
        <Carousel
          setApi={setApi}
          opts={{
            align: 'center',
            dragFree: true,
            containScroll: 'trimSnaps',
            loop: false
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-0 pb-4 gap-4">
            {effectivePromotions.map((promo) => (
              <CarouselItem
                key={promo.id}
                className="pl-0 basis-auto flex-shrink-0 max-w-[1200px] lg:w-[900px] md:w-[700px] sm:w-[550px] w-[350px]"
              >
                <PromotionBanner
                  title={promo.title}
                  description={promo.description}
                  buttonLabel={promo.buttonLabel}
                  onButtonClick={() => handleButtonClick(promo.id)}
                  imageUrl={promo.imageUrl}
                  className="h-full"
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>

      {/* Dynamic bullet navigation. Visible dot stays 8-10 px but the
          interactive <button> has a 24×24 hit area to pass Lighthouse's
          target-size audit. The dot is rendered as a child span; the
          button itself is transparent. */}
      <div className="flex justify-center items-center mt-4">
        {effectivePromotions.map((promo, index) => {
          const isActive = index === current;
          return (
            <button
              key={`bullet-${promo.id}`}
              type="button"
              onClick={() => scrollToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
              aria-current={isActive ? 'true' : undefined}
              className="inline-flex items-center justify-center w-6 h-6 bg-transparent cursor-pointer focus:outline-none"
            >
              <span
                aria-hidden="true"
                className={cn(
                  'block rounded-full transition-all duration-300 ease-in-out',
                  isActive ? 'w-2.5 h-2.5 bg-primary-500' : 'w-2 h-2 bg-neutral-400 opacity-50'
                )}
              />
            </button>
          );
        })}
      </div>
    </Section>
  );
};
